import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';

import { SimpleDropdownConfig } from './interfaces/simple-dropdown-config.interface';
import { SimpleDropdownItem } from 'Angular/features/components/dropdowns/simple-dropdown/interfaces/simple-dropdown-item.interface';

import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';

/**
 *
 * Simple Dropdown Component
 *
 * This component is responsible for displaying a scrollable list of items which may be visually grouped. Clicking an
 * item selects it and closes the drop down.
 *
 *
 *  * Input Properties
 *   buttonClass: The CSS class to be applied to the button that opens the dropdown
 *   config: The config object, specifying how the dropdown title and items should be configured.
 *   dropdownClass: The CSS class to be applied to the dropdown menu itself
 *   externalToggle: HTMLElement that is external to this component to be used as the dropdown toggle.
 *   inputDisabled: Whether the dropdown should be disabled
 *   showDropdownCaret: Whether the dropdown caret/arrow should be visible. Set this to false when you want to use a different icon as the dropdown toggle.
 *   closeOnSubmit: Whether to close the dropdown automatically when an option is selected
 *   tooltipText: a small descriptive message or text label that is displayed when users hover their
 *   mouse over view.
 *
 *
 *  Output properties
 *    handler: EventEmitter that emits the item's value whenever a new item is selected
 *
 */

@Component({ standalone: false, selector: 'ycn-simple-dropdown', templateUrl: './simple-dropdown.template.html' })
export class SimpleDropdownComponent implements AfterViewInit {
    @ViewChild('dropdownMenu') dropdownMenu!: ElementRef<HTMLElement>;

    @Input() appendToBody: boolean = false;

    @Input() buttonClass!: string;

    @Input() config!: SimpleDropdownConfig;

    @Input() dropdownClass!: string;

    @Input() inputDisabled: boolean = false;

    @Input() showDropdownCaret: boolean = true;

    @Input() closeOnSubmit: boolean = true;

    @Input() tooltipText?: string;

    @Input() externalToggle?: HTMLElement;

    @Output() handler = new EventEmitter<string>();

    @Output() isOpenChange = new EventEmitter<boolean>(false);

    private _isOpen: boolean = false;

    get isOpen(): boolean {
        return this._isOpen;
    }

    set isOpen(value: boolean) {
        this._isOpen = value;
        this.isOpenChange.emit(value);
    }

    constructor(
        public el: ElementRef,
        private dropdownHelperService: DropdownHelperService,
        private cd: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        const autoCloseHandler = (): void => {
            if (this.isOpen) {
                this.isOpen = false;
            }
            this.cd.detectChanges();
        };
        if (this.appendToBody) {
            this.dropdownHelperService.setCloseHandler(this.dropdownMenu.nativeElement, autoCloseHandler);
        } else {
            this.dropdownHelperService.setCloseHandler(this.externalToggle ?? this.el.nativeElement, autoCloseHandler);
        }
    }

    toggleOpen(): void {
        if ((!this.inputDisabled || this.isOpen) && !this.externalToggle) {
            this.isOpen = !this.isOpen;
        }
    }

    submit(listItem: SimpleDropdownItem): void {
        if (!listItem.disabled && listItem.value) {
            this.handler.emit(listItem.value);
        }
        if (this.closeOnSubmit) {
            this.isOpen = false;
        }
    }
}
